/**
 * UI adapter: bridges React economy handlers to LaunchProduct / ProcessLoan / research / pitch use cases
 * Enable via architectureFeatures.useEconomyUseCases
 */
import { useCallback } from 'react';
import {
  launchProductUseCase,
  processLoanUseCase,
  startResearchProjectUseCase,
  pitchInvestorsUseCase,
} from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { LaunchProductUseCase } from '../../../application/use-cases/LaunchProductUseCase';
import { ProcessLoanUseCase } from '../../../application/use-cases/ProcessLoanUseCase';
import { StartResearchProjectUseCase } from '../../../application/use-cases/StartResearchProjectUseCase';
import { PitchInvestorsUseCase } from '../../../application/use-cases/PitchInvestorsUseCase';
import type {
  LaunchProductInput,
  LaunchProductOutput,
  ProcessLoanInput,
  ProcessLoanOutput,
  StartResearchProjectInput,
  StartResearchProjectOutput,
  PitchInvestorsInput,
  PitchInvestorsOutput,
} from '../../../application/dto/EconomyDto';

export type {
  LaunchProductInput,
  LaunchProductOutput,
  ProcessLoanInput,
  ProcessLoanOutput,
  StartResearchProjectInput,
  StartResearchProjectOutput,
  PitchInvestorsInput,
  PitchInvestorsOutput,
};

export function executeLaunchProduct(input: LaunchProductInput): LaunchProductOutput {
  if (!architectureFeatures.useEconomyUseCases) {
    throw new Error('useEconomyUseCases feature flag is disabled');
  }
  return launchProductUseCase.execute(input);
}

export function executeProcessLoan(input: ProcessLoanInput): ProcessLoanOutput {
  if (!architectureFeatures.useEconomyUseCases) {
    throw new Error('useEconomyUseCases feature flag is disabled');
  }
  return processLoanUseCase.execute(input);
}

export function executeStartResearchProject(
  input: StartResearchProjectInput,
): StartResearchProjectOutput {
  if (!architectureFeatures.useEconomyUseCases) {
    throw new Error('useEconomyUseCases feature flag is disabled');
  }
  return startResearchProjectUseCase.execute(input);
}

export function executePitchInvestors(input: PitchInvestorsInput): PitchInvestorsOutput {
  if (!architectureFeatures.useEconomyUseCases) {
    throw new Error('useEconomyUseCases feature flag is disabled');
  }
  return pitchInvestorsUseCase.execute(input);
}

export function executeLegacyLaunchProduct(input: LaunchProductInput): LaunchProductOutput {
  return new LaunchProductUseCase().execute(input);
}

export function executeLegacyProcessLoan(input: ProcessLoanInput): ProcessLoanOutput {
  return new ProcessLoanUseCase().execute(input);
}

export function executeLegacyStartResearchProject(
  input: StartResearchProjectInput,
): StartResearchProjectOutput {
  return new StartResearchProjectUseCase().execute(input);
}

export function executeLegacyPitchInvestors(input: PitchInvestorsInput): PitchInvestorsOutput {
  return new PitchInvestorsUseCase().execute(input);
}

export function resolveLaunchProduct(input: LaunchProductInput): LaunchProductOutput {
  if (architectureFeatures.useEconomyUseCases) {
    return executeLaunchProduct(input);
  }
  return executeLegacyLaunchProduct(input);
}

export function resolveProcessLoan(input: ProcessLoanInput): ProcessLoanOutput {
  if (architectureFeatures.useEconomyUseCases) {
    return executeProcessLoan(input);
  }
  return executeLegacyProcessLoan(input);
}

export function resolveStartResearchProject(
  input: StartResearchProjectInput,
): StartResearchProjectOutput {
  if (architectureFeatures.useEconomyUseCases) {
    return executeStartResearchProject(input);
  }
  return executeLegacyStartResearchProject(input);
}

export function resolvePitchInvestors(input: PitchInvestorsInput): PitchInvestorsOutput {
  if (architectureFeatures.useEconomyUseCases) {
    return executePitchInvestors(input);
  }
  return executeLegacyPitchInvestors(input);
}

/**
 * Hook helper for useEconomyHandlers integration.
 * Returns stable callbacks that run economy use cases when feature flag is on.
 */
export function useEconomyMutationRunner() {
  const runLaunchProduct = useCallback(
    (input: LaunchProductInput): LaunchProductOutput | null => {
      if (!architectureFeatures.useEconomyUseCases) {
        return null;
      }
      return executeLaunchProduct(input);
    },
    [],
  );

  const runProcessLoan = useCallback(
    (input: ProcessLoanInput): ProcessLoanOutput | null => {
      if (!architectureFeatures.useEconomyUseCases) {
        return null;
      }
      return executeProcessLoan(input);
    },
    [],
  );

  const runStartResearchProject = useCallback(
    (input: StartResearchProjectInput): StartResearchProjectOutput | null => {
      if (!architectureFeatures.useEconomyUseCases) {
        return null;
      }
      return executeStartResearchProject(input);
    },
    [],
  );

  const runPitchInvestors = useCallback(
    (input: PitchInvestorsInput): PitchInvestorsOutput | null => {
      if (!architectureFeatures.useEconomyUseCases) {
        return null;
      }
      return executePitchInvestors(input);
    },
    [],
  );

  return { runLaunchProduct, runProcessLoan, runStartResearchProject, runPitchInvestors };
}